import { apiClient } from './client'
import type {
  CurrentUser,
  LoginRequest,
  LoginResponseRaw,
  LoginResult,
  RefreshRequest,
  RefreshResponseRaw,
} from './types'

/**
 * Streama CMS · Auth endpoints
 *
 * Typed wrappers over the deployed backend's /api/cms/users/* surface.
 *
 * Endpoint paths reflect the live backend (captured in v2 runbook):
 *   POST /api/cms/users/login    ✓ live (Sprint 1 IAM)
 *   POST /api/cms/users/refresh  ✗ TBD — currently 404s; poller swallows
 *   POST /api/cms/users/logout   ✗ TBD — currently 404s; UI swallows
 *   GET  /api/cms/users/me       ✗ TBD — auth store reconstructs from JWT
 *
 * The deployed backend returns `expiresInSeconds: number` rather than
 * an absolute `expiresAt: ISO string`. The login() wrapper computes
 * the absolute timestamp at the SPA layer so the rest of the app
 * works in absolute time (simpler refresh-poller math, easier to
 * reason about across timezones).
 */

export const authApi = {
  /**
   * Authenticate with username + password.
   *
   * Returns a normalised `LoginResult` with an absolute `expiresAt`
   * (ISO-8601, computed from `expiresInSeconds` at the moment of
   * response receipt). Privileges arrive as bare names — no PRIV_
   * prefix; that prefix is internal to Spring Security and never
   * crosses the wire.
   *
   * On bad credentials throws ApiException with status=401 and
   * code='INVALID_CREDENTIALS'. Backend returns identical error shape
   * for unknown user vs wrong password (anti-enumeration; Sprint 1
   * fix T05).
   */
  async login(req: LoginRequest): Promise<LoginResult> {
    const res = await apiClient.post<LoginResponseRaw>(
      '/cms/users/login',
      req,
    )
    const raw = res.data
    return {
      token: raw.token,
      expiresAt: computeExpiresAt(raw.expiresInSeconds),
      user: raw.user,
    }
  },

  /**
   * Exchange a current (non-expired) token for a fresh one.
   *
   * Backend status: NOT YET IMPLEMENTED. Returns 404. The auth
   * store's refresh poller catches the failure silently — the
   * existing token continues to work until real expiry. Once the
   * backend endpoint lands this method starts succeeding without
   * UI changes.
   */
  async refresh(req: RefreshRequest): Promise<LoginResult> {
    const res = await apiClient.post<RefreshResponseRaw>(
      '/cms/users/refresh',
      req,
    )
    const raw = res.data
    return {
      token: raw.token,
      expiresAt: computeExpiresAt(raw.expiresInSeconds),
      user: raw.user,
    }
  },

  /**
   * Best-effort token invalidation. Stateless JWT can't actually be
   * invalidated without a deny-list, but having the endpoint lets
   * the backend add one later without UI changes. Swallows errors —
   * logout must always succeed from the user's perspective.
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/cms/users/logout')
    } catch {
      // Swallow.
    }
  },

  /**
   * Current user + privileges. Used on app boot when the token is
   * valid but the in-memory user is missing.
   *
   * Backend status: NOT YET IMPLEMENTED. The auth store reconstructs
   * CurrentUser from the JWT claims cached at login. This wrapper
   * exists so the migration to the real endpoint is one bean swap.
   */
  async me(): Promise<CurrentUser> {
    const res = await apiClient.get<CurrentUser>('/cms/users/me')
    return res.data
  },
}

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------

/**
 * Convert backend's relative TTL into an absolute timestamp.
 * Subtracts a 5-second safety margin so the SPA's expiry checks
 * fire slightly before the backend would actually reject the token,
 * avoiding a race window where the SPA thinks the token is valid
 * but the next call gets rejected.
 */
function computeExpiresAt(expiresInSeconds: number): string {
  const SAFETY_MARGIN_MS = 5_000
  const expiryMs = Date.now() + expiresInSeconds * 1000 - SAFETY_MARGIN_MS
  return new Date(expiryMs).toISOString()
}
